import { createId } from '@/lib/table-utils';
import type {
    PublisherResultImport,
    PublisherResultProviderTable,
    PublisherResultTableEntry,
} from '@/types/publisher-result-import';
import type { TableBlock, TableCell } from '@/types/report-payload';

const DEFAULT_COLUMN_WIDTH = 150;

const positionKey = (section: 'header' | 'body', rowIndex: number, columnIndex: number) =>
    `${section}:${rowIndex}:${columnIndex}`;

export const getPublisherResultTableEntries = (
    record: PublisherResultImport,
): PublisherResultTableEntry[] => {
    const extraction = record.extraction_json;
    if (!extraction?.instruments) return [];

    return extraction.instruments.flatMap((instrument) =>
        instrument.tables.map((table) => ({ instrument, table })),
    );
};

export const getDefaultSelectedPublisherResultTableIds = (record: PublisherResultImport) => {
    const entries = getPublisherResultTableEntries(record);
    const preferred = entries
        .filter(({ table }) => table.include_in_report !== false)
        .map(({ table }) => table.id);

    if (preferred.length > 0) return preferred;

    return entries.filter(({ table }) => table.is_main).map(({ table }) => table.id);
};

const buildCell = (
    cell: PublisherResultProviderTable['header']['cells'][number],
    section: 'header' | 'body',
    rowIndex: number,
    columnIndex: number,
    cellIdsByPosition: Map<string, string>,
): TableCell => {
    const id = cellIdsByPosition.get(positionKey(section, rowIndex, columnIndex));
    if (!id) {
        return {
            id: createId('cell'),
            value: cell.value ?? '',
        };
    }

    if (cell.mergedInto) {
        const mergedInto = cellIdsByPosition.get(
            positionKey(
                cell.mergedInto.section,
                cell.mergedInto.rowIndex,
                cell.mergedInto.columnIndex,
            ),
        );

        return {
            id,
            value: cell.value ?? '',
            ...(mergedInto ? { mergedInto } : {}),
        };
    }

    return {
        id,
        value: cell.value ?? '',
        ...(cell.rowSpan ? { rowSpan: cell.rowSpan } : {}),
        ...(cell.colSpan ? { colSpan: cell.colSpan } : {}),
    };
};

export const createTableBlockFromPublisherResult = (input: {
    table: PublisherResultProviderTable;
    order: number;
    description: string;
    sourceImportId: string;
    sourceInstrumentResultId: string;
    sourceImportTableId: string;
}): TableBlock => {
    const columnCount = Math.max(input.table.header.cells.length, 1);
    const cellIdsByPosition = new Map<string, string>();

    // A pilha do cabeçalho, faixas primeiro e folha por último — a mesma ordem
    // que o `rowIndex` de um `mergedInto` usa do lado da API.
    const extractedHeaderRows = [...(input.table.header_bands ?? []), input.table.header];

    extractedHeaderRows.forEach((headerRow, rowIndex) => {
        headerRow.cells.forEach((_cell, columnIndex) => {
            cellIdsByPosition.set(positionKey('header', rowIndex, columnIndex), createId('cell'));
        });
    });

    input.table.rows.forEach((row, rowIndex) => {
        row.cells.forEach((_cell, columnIndex) => {
            cellIdsByPosition.set(positionKey('body', rowIndex, columnIndex), createId('cell'));
        });
    });

    return {
        id: createId('table'),
        block_type: 'table',
        order: input.order,
        has_content: true,
        description: input.description,
        automatic: false,
        ...(input.table.header_bands?.length
            ? {
                  header_bands: input.table.header_bands.map((band, rowIndex) => ({
                      id: createId('row'),
                      cells: Array.from({ length: columnCount }, (_, columnIndex) => {
                          const cell = band.cells[columnIndex] ?? { value: '' };
                          return buildCell(
                              cell,
                              'header',
                              rowIndex,
                              columnIndex,
                              cellIdsByPosition,
                          );
                      }),
                  })),
              }
            : {}),
        header: {
            id: createId('row'),
            cells: input.table.header.cells.map((cell, columnIndex) =>
                buildCell(
                    cell,
                    'header',
                    extractedHeaderRows.length - 1,
                    columnIndex,
                    cellIdsByPosition,
                ),
            ),
        },
        rows: input.table.rows.map((row, rowIndex) => ({
            id: createId('row'),
            cells: Array.from({ length: columnCount }, (_, columnIndex) => {
                const cell = row.cells[columnIndex] ?? { value: '' };
                return buildCell(cell, 'body', rowIndex, columnIndex, cellIdsByPosition);
            }),
        })),
        columnWidths:
            input.table.columnWidths?.length === columnCount
                ? input.table.columnWidths
                : Array.from({ length: columnCount }, () => DEFAULT_COLUMN_WIDTH),
        source_import_id: input.sourceImportId,
        source_instrument_result_id: input.sourceInstrumentResultId,
        source_import_table_id: input.sourceImportTableId,
    };
};

export const isPublisherResultImportActive = (record: PublisherResultImport) =>
    ['uploaded', 'queued', 'extracting_text', 'deidentifying', 'extracting_tables'].includes(
        record.status,
    );
